import React from 'react';
import { Button, Popconfirm } from 'antd';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { INETWORK_CONFIGURES } from '@api/emp/types';
import { NetworkConfigSummary } from '../utils/formatUtils.tsx';

interface NetworkConfigViewProps {
  config: INETWORK_CONFIGURES;
  index: number;
  onEdit: (config: INETWORK_CONFIGURES) => void;
  onDelete: (configId: string) => void;
  isUpdating?: boolean;
  isDeleting?: boolean;
  disabled?: boolean;
}

const NetworkConfigView: React.FC<NetworkConfigViewProps> = ({
  config,
  index,
  onEdit,
  onDelete,
  isUpdating = false,
  isDeleting = false,
  disabled = false,
}) => {
  const updatedDate = config.updated_at
    ? new Date(config.updated_at * 1000)
    : new Date(config.created_at * 1000);

  const updatedBy = config.updated_by || config.created_by || '';

  return (
    <div
      style={{
        marginBottom: 8,
        borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
        borderLeft: '3px solid #0046FF',
        paddingBottom: 12,
      }}
      data-test-id={`network-config-rule-${index}`}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          padding: '4px 16px 0px 16px',
        }}
      >
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start' }}>
            <div
              style={{
                width: 24,
                height: 24,
                borderRadius: '50%',
                backgroundColor: '#f0f0f0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '12px',
                fontWeight: 500,
                color: '#666',
                marginRight: '12px',
                flexShrink: 0,
              }}
            >
              {index + 1}
            </div>

            <div
              style={{
                fontSize: '14px',
                fontWeight: '500',
                fontFamily: 'monospace',
                color: '#495057',
                lineHeight: '1.5',
                wordBreak: 'break-word',
                flex: 1,
              }}
              data-test-id="network-config-rule-summary"
            >
              <NetworkConfigSummary config={config} />
            </div>
          </div>

          <div
            style={{
              marginLeft: '36px',
              marginTop: '4px',
              fontSize: '12px',
              color: '#999',
            }}
            data-test-id="network-config-rule-updated-at"
          >
            Last Updated on{' '}
            {updatedDate.toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })}{' '}
            {updatedBy && `by ${updatedBy}`}
          </div>
        </div>

        {!disabled && (
          <div style={{ display: 'flex', gap: 8 }}>
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => onEdit(config)}
              loading={isUpdating}
              disabled={isUpdating || isDeleting}
              data-test-id="network-config-edit-button"
            />
            <Popconfirm
              title="Are you sure you want to delete this configuration?"
              okText="Yes"
              cancelText="No"
              placement="topRight"
              onConfirm={() => onDelete(config.id)}
              okButtonProps={{
                loading: isDeleting,
                danger: true,
              }}
            >
              <Button
                type="text"
                size="small"
                icon={<DeleteOutlined />}
                disabled={isUpdating || isDeleting}
                danger
                data-test-id="network-config-delete-button"
              />
            </Popconfirm>
          </div>
        )}
      </div>
    </div>
  );
};

export default NetworkConfigView;
